/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Switch } from 'antd'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import BetaBadge from 'src/components/ts-components/BetaBadge'
import Text from 'src/components/ts-components/Text'
import QuestionIcon from 'src/components/icons/QuestionIcon'
import RichTooltip from 'src/components/ts-components/RichTooltip'
import { font, paragraphSmall } from 'src/components/css'

const DESCRIPTION =
  'Player Finder is a tool that connects players with teams. If you are interested in being a spare or joining another team.'

const OPT_IN_EXPLANATION =
  'Opt in and teams looking for players can find you, see the sports and positions you set below, and invite you. Opt out any time — you are never listed until you turn this on.'

const PLAYER_FINDER_SUPPORT_URL = 'https://support.sportninja.com/player-finder'

interface Props {
  value: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}

const OptInHeaderCard: React.FC<Props> = ({ value, onChange, disabled }) => (
  <div css={cardStyle}>
    <div css={switchRowStyle}>
      <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
        {capitalizeWords('Opt In For Player Finder')}
      </Text>
      <Switch
        checked={value}
        onChange={onChange}
        disabled={disabled}
        size='small'
        css={switchStyle}
      />
      <BetaBadge />
      {/* The hint used to read "Learn more about Player Finder" — a tooltip whose
          only content was an instruction to go read elsewhere. It now answers the
          question in place; the link stays for the full article. */}
      <RichTooltip
        title='Player Finder'
        body={DESCRIPTION}
        next={OPT_IN_EXPLANATION}
        focusable={false}
        // Above the profile modal wrapper (10001) — antd's 1070 default would
        // open this behind it.
        zIndex={10050}
      >
        <a
          css={infoButtonStyle}
          href={PLAYER_FINDER_SUPPORT_URL}
          target='_blank'
          rel='noopener noreferrer'
          aria-label='Learn more about Player Finder'
        >
          <QuestionIcon size={18} color={colors.PRIMARY} />
        </a>
      </RichTooltip>
    </div>
    <div css={dividerStyle} />
    <div css={descriptionStyle}>{DESCRIPTION}</div>
  </div>
)

export default OptInHeaderCard

// ─── Styles ────────────────────────────────────────────────────────────────

const cardStyle = css`
  background-color: ${colors.SECONDARY_400};
  border-radius: 12px;
  border: 1px solid ${colors.NEUTRAL_700};
`

// Title + toggle sit together on one line (the toggle immediately follows the
// title text). Using antd Switch directly — not SwitchInput — because that
// component's title is flex:1 and would push the switch to the far right.
const switchRowStyle = css`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
`

// Clickable help icon (opens the Player Finder support page in a new tab),
// matching the family Invite/Edit form's info-icon affordance.
const infoButtonStyle = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  line-height: 0;

  &:hover {
    opacity: 0.85;
  }
`

// Match the app's switch treatment (PRIMARY when on, white handle).
const switchStyle = css`
  &.ant-switch-checked {
    background-color: ${colors.PRIMARY};
  }
  &.ant-switch-checked:hover:not(.ant-switch-disabled) {
    background-color: ${colors.PRIMARY};
    opacity: 0.8;
  }
`

const dividerStyle = css`
  height: 1px;
  background-color: ${colors.SOFT_STEEL};
`

const descriptionStyle = css`
  ${paragraphSmall}
  ${font.body}
  color: ${colors.NEUTRAL_50};
  padding: 16px;
`
